// Phase 19 — Store products page (affiliate / lifted_partner).
// Lists every product the selected partner store carries; each row offers
// a quick "Create affiliate link" CTA that hits POST /api/affiliate/me/product-links/create.

import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, Truck, ShieldAlert, ShieldCheck, Link2, Copy, Check,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { ProductImg } from "@/lib/productImage";

function absoluteFromLink(link) {
  if (!link) return "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const path = link.short_path || link.referral_path
    || link.referral_url || link.short_url || "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${origin}${path.startsWith("/") ? "" : "/"}${path}`;
}

async function copyToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) { return false; }
}

export default function LiftedSuppliesStorePage() {
  const { storeId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState("");
  const [createdLinks, setCreatedLinks] = useState({}); // {handle: link}
  const [copied, setCopied] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/lifted-supplies/me/stores/${storeId}/products`);
      setData(data);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  const createLink = async (p) => {
    setCreating(p.handle);
    try {
      const { data } = await api.post("/affiliate/me/product-links/create", {
        product_handle: p.handle,
        product_id: p.id,
        product_slug: p.handle,
        same_day: !!p.same_day_eligible,
      });
      setCreatedLinks((s) => ({ ...s, [p.handle]: data.link }));
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setCreating(""); }
  };

  const copyExisting = async (p) => {
    let link = createdLinks[p.handle];
    if (!link) { await createLink(p); link = createdLinks[p.handle]; }
    if (!link) return;
    const ok = await copyToClipboard(absoluteFromLink(link));
    if (ok) {
      setCopied(p.handle);
      setTimeout(() => setCopied(""), 1500);
    }
  };

  if (err) return <div className="p-6 text-rose-300" data-testid="lss-error">{err}</div>;
  if (!data) return (
    <div className="p-6 text-lifted-dim flex items-center gap-2" data-testid="lss-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading store…
    </div>
  );

  return (
    <div className="space-y-4" data-testid="lss-store-page">
      <Link to="/lifted-partner" className="inline-flex items-center text-xs text-lifted-green hover:underline">
        <ArrowLeft className="h-3 w-3 mr-1" /> Back
      </Link>
      <header>
        <div className="text-lifted-green font-bebas tracking-[0.22em] text-[0.65rem]">LIFTED SUPPLIES · STORE</div>
        <h1 className="font-anton text-3xl uppercase text-white">
          {data.store?.name || "Store"}
        </h1>
        <div className="text-sm text-lifted-dim">
          {[data.store?.city, data.store?.state].filter(Boolean).join(", ")}
        </div>
      </header>

      <section className="glass-panel overflow-hidden">
        <div className="px-3 py-2 border-b border-white/5 font-bebas tracking-[0.22em] text-xs text-white">
          PRODUCTS · {data.count}
        </div>
        {data.products.length === 0 ? (
          <div className="p-6 text-sm text-lifted-dim">
            No products listed by this store yet.
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {data.products.map((p) => (
              <li key={p.handle} className="p-3 flex items-center gap-3" data-testid={`lss-prod-${p.handle}`}>
                <div className="h-12 w-12 rounded bg-black/40 overflow-hidden border border-white/10 grid place-items-center shrink-0">
                  <ProductImg product={p} className="h-full w-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{p.title}</div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                    <span className="text-[0.7rem] text-lifted-green">${((p.price_cents || 0) / 100).toFixed(2)}</span>
                    {p.same_day_eligible && (
                      <span className="text-[0.6rem] text-amber-300 inline-flex items-center gap-1">
                        <Truck className="h-2.5 w-2.5" /> Same-day
                      </span>
                    )}
                    {p.state_blocked ? (
                      <span className="text-[0.6rem] text-rose-300 inline-flex items-center gap-1">
                        <ShieldAlert className="h-2.5 w-2.5" /> Restricted
                      </span>
                    ) : (
                      p.compliance_status === "compliant" && (
                        <span className="text-[0.6rem] text-lifted-green inline-flex items-center gap-1">
                          <ShieldCheck className="h-2.5 w-2.5" />
                        </span>
                      )
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => createLink(p)} disabled={creating === p.handle}
                          className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`lss-link-${p.handle}`}>
                    {creating === p.handle ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
                    Affiliate link
                  </button>
                  {createdLinks[p.handle] && (
                    <button onClick={() => copyExisting(p)}
                            className="btn-ghost text-xs inline-flex items-center gap-1"
                            data-testid={`lss-copy-${p.handle}`}>
                      {copied === p.handle ? <Check className="h-3 w-3 text-lifted-green" /> : <Copy className="h-3 w-3" />}
                      {copied === p.handle ? "Copied" : "Copy"}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
